import type { ResumoDashboard } from "@base/contracts";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  dadosStatusPipeline,
  formatarDelta,
  formatarNumero,
  formatarPercentual,
  ROTULOS_STATUS,
} from "./dashboard-formatadores";
import {
  BarraCapacidade,
  ChipStatus,
  FaixaAlerta,
  MetricaCompacta,
  NumeroHero,
  Painel,
  PainelCabecalho,
  PainelCorpo,
} from "./dashboard-ui";

const chartThroughputConfig = {
  quantidade: { label: "Posts", color: "var(--chart-1)" },
} satisfies ChartConfig;

export function AbaOperacao({ resumo }: { readonly resumo: ResumoDashboard }) {
  const status = dadosStatusPipeline(resumo);
  const maxStatus = Math.max(1, ...status.map((s) => s.quantidade));
  const throughput = resumo.produtividade.throughputPorPessoa.map((item) => ({
    nome:
      item.responsavelNome ??
      (item.responsavelId ? "Responsável" : "Sem responsável"),
    quantidade: item.quantidade,
  }));
  const ocupacaoPct = Math.min(
    100,
    Math.round(resumo.ocupacao.percentualOcupacao),
  );
  const fidelidadePct =
    resumo.fidelidade.taxaFidelidade === null
      ? 0
      : Math.round(resumo.fidelidade.taxaFidelidade * 100);
  const ciclo =
    resumo.produtividade.tempoCicloMedioDias !== null
      ? `${resumo.produtividade.tempoCicloMedioDias.toFixed(1)} d`
      : "Sem dados";
  const deltaPublicados = resumo.performance.comparativoPeriodo?.publicados;

  return (
    <div className="flex flex-col gap-8">
      {resumo.alertas.length > 0 ? (
        <section className="flex flex-col gap-3" aria-label="Alertas">
          {resumo.alertas.slice(0, 3).map((alerta) => (
            <FaixaAlerta
              key={alerta.id}
              titulo={alerta.titulo}
              descricao={alerta.descricao}
              severidade={alerta.severidade === "alta" ? "alta" : "media"}
              {...(alerta.acaoHref
                ? {
                    acao: (
                      <a
                        href={alerta.acaoHref}
                        className="cursor-pointer text-sm font-medium text-foreground underline-offset-4 hover:underline"
                      >
                        Ver detalhes
                      </a>
                    ),
                  }
                : {})}
            />
          ))}
        </section>
      ) : null}

      <Painel className="dashboard-hero" aria-label="Indicadores principais">
        <div className="relative z-[1] grid gap-0 md:grid-cols-12">
          <div className="flex flex-col justify-between gap-8 border-b border-border/60 p-6 sm:p-8 md:col-span-7 md:border-b-0 md:border-r">
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">
                Ocupação da equipe
              </p>
              <NumeroHero tom={resumo.ocupacao.sobrecarga ? "alerta" : "padrao"}>
                {ocupacaoPct}%
              </NumeroHero>
              <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
                {resumo.ocupacao.cargaAtual} de {resumo.ocupacao.capacidadePeriodo}{" "}
                slots ativos no período.
                {resumo.ocupacao.sobrecarga
                  ? " Alguém passou do limite."
                  : " Ainda dentro da capacidade."}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <ChipStatus tom={resumo.ocupacao.sobrecarga ? "alerta" : "ok"}>
                {resumo.ocupacao.sobrecarga ? "Sobrecarga" : "No limite"}
              </ChipStatus>
              <ChipStatus tom="marca">
                Fidelidade {formatarPercentual(resumo.fidelidade.taxaFidelidade)}
              </ChipStatus>
            </div>
            <BarraCapacidade
              valor={ocupacaoPct}
              rotulo="Carga vs capacidade"
              alerta={resumo.ocupacao.sobrecarga}
            />
          </div>

          <div className="grid grid-cols-1 divide-y divide-border/60 md:col-span-5 md:px-6 md:py-2">
            <div className="px-6 py-4 md:px-0">
              <MetricaCompacta
                rotulo="Tempo de ciclo"
                valor={ciclo}
                detalhe="Criação até publicação"
              />
            </div>
            <div className="px-6 py-4 md:px-0">
              <MetricaCompacta
                rotulo="Alcance médio"
                valor={formatarNumero(resumo.performance.alcanceMedio)}
                detalhe="Posts no período"
                {...(resumo.performance.comparativoPeriodo
                  ? {
                      delta: formatarDelta(
                        resumo.performance.comparativoPeriodo.alcance
                          .deltaPercentual,
                      ),
                    }
                  : {})}
              />
            </div>
            <div className="px-6 py-4 md:px-0">
              <MetricaCompacta
                rotulo="Fidelidade ao plano"
                valor={formatarPercentual(resumo.fidelidade.taxaFidelidade)}
                detalhe={`${resumo.fidelidade.publicadosNoPrazo}/${resumo.fidelidade.planejados} no prazo`}
                alerta={
                  resumo.fidelidade.taxaFidelidade !== null &&
                  resumo.fidelidade.taxaFidelidade < 0.7
                }
              />
            </div>
            <div className="px-6 py-4 md:px-0">
              <MetricaCompacta
                rotulo="Publicados vs anterior"
                valor={
                  deltaPublicados
                    ? formatarNumero(deltaPublicados.atual)
                    : formatarNumero(0)
                }
                detalhe="Volume no recorte atual"
                {...(deltaPublicados
                  ? { delta: formatarDelta(deltaPublicados.deltaPercentual) }
                  : {})}
              />
            </div>
          </div>
        </div>
      </Painel>

      <section
        className="grid gap-6 lg:grid-cols-12"
        aria-label="Pipeline e capacidade"
      >
        <Painel className="lg:col-span-7">
          <PainelCabecalho
            titulo="Pipeline editorial"
            descricao="Distribuição por status no fluxo atual."
          />
          <PainelCorpo className="space-y-5">
            {status.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Sem itens no pipeline.
              </p>
            ) : (
              status.map((item) => (
                <div key={item.status} className="space-y-2">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium text-foreground">
                      {item.rotulo}
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {item.quantidade}
                    </span>
                  </div>
                  <div
                    className="h-2 overflow-hidden rounded-full bg-hatch"
                    role="presentation"
                  >
                    <div
                      className="h-full rounded-full bg-primary transition-[width] duration-500 motion-reduce:transition-none"
                      style={{
                        width: `${(item.quantidade / maxStatus) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              ))
            )}
          </PainelCorpo>
        </Painel>

        <Painel className="lg:col-span-5">
          <PainelCabecalho
            titulo="Capacidade do período"
            descricao="Carga total e fidelidade lado a lado."
            acao={
              <ChipStatus tom={resumo.ocupacao.sobrecarga ? "alerta" : "ok"}>
                {resumo.ocupacao.sobrecarga ? "Sobrecarga" : "No limite"}
              </ChipStatus>
            }
          />
          <PainelCorpo className="space-y-8">
            <div className="space-y-3">
              <NumeroHero className="text-3xl sm:text-4xl">
                {ocupacaoPct}%
              </NumeroHero>
              <BarraCapacidade
                valor={ocupacaoPct}
                rotulo="Ocupação"
                alerta={resumo.ocupacao.sobrecarga}
              />
            </div>
            <BarraCapacidade
              valor={fidelidadePct}
              rotulo="Fidelidade ao plano"
              alerta={fidelidadePct > 0 && fidelidadePct < 70}
            />
          </PainelCorpo>
        </Painel>
      </section>

      <Painel aria-label="Carga por pessoa">
        <PainelCabecalho
          titulo="Carga por pessoa"
          descricao="Posts ativos versus o teto do período. Quem passou aparece em vermelho."
        />
        <PainelCorpo semPadding>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-6">Pessoa</TableHead>
                <TableHead>Carga</TableHead>
                <TableHead className="w-[38%]">Ocupação</TableHead>
                <TableHead className="pr-6 text-right">Situação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {resumo.ocupacao.porPessoa.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="pl-6 text-muted-foreground">
                    Ninguém com posts atribuídos neste período.
                  </TableCell>
                </TableRow>
              ) : (
                resumo.ocupacao.porPessoa.map((pessoa) => {
                  const nome =
                    pessoa.responsavelNome ??
                    (pessoa.responsavelId ? "Responsável" : "Sem responsável");
                  const pct = Math.min(
                    100,
                    Math.round(pessoa.percentualOcupacao),
                  );
                  return (
                    <TableRow key={pessoa.responsavelId ?? "sem-responsavel"}>
                      <TableCell className="pl-6 font-medium">{nome}</TableCell>
                      <TableCell className="tabular-nums">
                        {pessoa.cargaAtual}/{pessoa.capacidadePeriodo}
                      </TableCell>
                      <TableCell>
                        <div
                          className="h-1.5 overflow-hidden rounded-full bg-hatch"
                          role="presentation"
                        >
                          <div
                            className={
                              pessoa.sobrecarga
                                ? "h-full rounded-full bg-destructive"
                                : "h-full rounded-full bg-primary"
                            }
                            style={{ width: `${pct}%` }}
                            aria-label={`${nome}: ${pessoa.cargaAtual} de ${pessoa.capacidadePeriodo}`}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="pr-6 text-right">
                        <Badge
                          variant={
                            pessoa.sobrecarga ? "destructive" : "secondary"
                          }
                        >
                          {pessoa.sobrecarga ? "Sobrecarga" : "No limite"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </PainelCorpo>
      </Painel>

      <section
        className="grid gap-6 xl:grid-cols-12"
        aria-label="Throughput e gargalos"
      >
        <Painel className="xl:col-span-5">
          <PainelCabecalho
            titulo="Throughput por pessoa"
            descricao="Volume publicado no período."
          />
          <PainelCorpo>
            {throughput.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Sem throughput no período.
              </p>
            ) : (
              <ChartContainer
                config={chartThroughputConfig}
                className="aspect-[16/10] w-full max-h-[260px]"
              >
                <BarChart
                  data={throughput}
                  layout="vertical"
                  margin={{ left: 8, right: 12, top: 4, bottom: 4 }}
                >
                  <CartesianGrid horizontal={false} strokeDasharray="3 3" />
                  <XAxis type="number" hide />
                  <YAxis
                    dataKey="nome"
                    type="category"
                    tickLine={false}
                    axisLine={false}
                    width={110}
                    fontSize={11}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar
                    dataKey="quantidade"
                    fill="var(--color-quantidade)"
                    radius={[0, 8, 8, 0]}
                  />
                </BarChart>
              </ChartContainer>
            )}
          </PainelCorpo>
        </Painel>

        <Painel className="xl:col-span-7">
          <PainelCabecalho
            titulo="Gargalos no pipeline"
            descricao="Conteúdos parados há mais tempo do que o combinado."
          />
          <PainelCorpo semPadding>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-6">Conteúdo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="pr-6 text-right">Dias</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resumo.produtividade.gargalos.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="pl-6 text-muted-foreground"
                    >
                      Nenhum gargalo no momento.
                    </TableCell>
                  </TableRow>
                ) : (
                  resumo.produtividade.gargalos.map((gargalo) => (
                    <TableRow key={gargalo.conteudoId}>
                      <TableCell className="pl-6 font-medium">
                        {gargalo.titulo}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {ROTULOS_STATUS[gargalo.status] ?? gargalo.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="pr-6 text-right">
                        <Badge variant="destructive">
                          {gargalo.diasParado}d
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </PainelCorpo>
        </Painel>
      </section>
    </div>
  );
}
